/**
 * Cross-Promotion Modal Component
 *
 * Displays cross-promotion content in a modal after adding a configurable product to cart
 */
class CrossPromotionModal {
    constructor() {
        this.modal = null;
        this.overlay = null;
        this.isOpen = false;
        this.onCloseCallback = null;
        
        // Bind methods
        this.open = this.open.bind(this);
        this.close = this.close.bind(this);
        this.handleKeyPress = this.handleKeyPress.bind(this);
        this.handleOverlayClick = this.handleOverlayClick.bind(this);
    }

    /**
     * Create the modal HTML structure
     * @param {string} content - HTML content for the modal
     * @returns {HTMLElement} Modal element
     */
    createModal(content) {
        // Create overlay
        this.overlay = document.createElement('div');
        this.overlay.className = 'cross-promotion-overlay';
        this.overlay.addEventListener('click', this.handleOverlayClick);

        // Create modal container
        this.modal = document.createElement('div');
        this.modal.className = 'cross-promotion-modal';
        this.modal.setAttribute('role', 'dialog');
        this.modal.setAttribute('aria-modal', 'true');
        this.modal.setAttribute('aria-labelledby', 'cross-promotion-title');

        // Create content container
        const contentContainer = document.createElement('div');
        contentContainer.className = 'cross-promotion-modal__content';
        contentContainer.innerHTML = content;

        // Assemble modal
        this.modal.appendChild(contentContainer);
        
        // Add click handlers to all links in the modal
        const links = contentContainer.querySelectorAll('a');
        links.forEach(link => {
            link.addEventListener('click', (e) => {
                // Prevent immediate navigation
                e.preventDefault();
                
                // Get the href for navigation
                const href = link.getAttribute('href');
                
                // Add loading spinner
                const spinner = document.createElement('span');
                spinner.className = 'cross-promotion-loading';
                
                // Disable all links to prevent multiple clicks
                links.forEach(l => {
                    l.style.pointerEvents = 'none';
                    l.style.opacity = '0.6';
                });
                
                // Add spinner after the clicked link
                link.appendChild(spinner);
                
                // Navigate after a short delay to show the spinner
                setTimeout(() => {
                    window.location.href = href;
                }, 300);
            });
        });

        return this.modal;
    }

    /**
     * Open the modal with the provided content
     * @param {string} content - HTML content for the modal
     * @param {Function} onClose - Callback function when modal closes
     * @returns {boolean} - Returns true if modal was opened, false if skipped
     */
    open(content, onClose = null) {
        if (this.isOpen) {
            return false;
        }

        // Check if sessionStorage is available
        try {
            const hasShownModal = sessionStorage.getItem('crossPromotionModalShown');
            if (hasShownModal) {
                // Modal already shown this session, do nothing
                return false;
            }
            // Mark modal as shown for this session
            sessionStorage.setItem('crossPromotionModalShown', 'true');
        } catch (e) {
            // sessionStorage not available, do nothing
            return false;
        }

        this.onCloseCallback = onClose;
        
        // Create modal with content
        this.createModal(content);

        // Add to DOM
        document.body.appendChild(this.overlay);
        document.body.appendChild(this.modal);

        // Prevent body scroll
        document.body.style.overflow = 'hidden';

        // Set open state
        this.isOpen = true;

        // Add keyboard event listener
        document.addEventListener('keydown', this.handleKeyPress);

        // Return true to indicate modal was opened
        return true;
    }

    /**
     * Close the modal
     */
    close() {
        if (!this.isOpen) {
            return;
        }

        // Remove from DOM
        if (this.overlay && this.overlay.parentNode) {
            this.overlay.parentNode.removeChild(this.overlay);
        }
        if (this.modal && this.modal.parentNode) {
            this.modal.parentNode.removeChild(this.modal);
        }

        // Restore body scroll
        document.body.style.overflow = '';

        // Set closed state
        this.isOpen = false;

        // Remove keyboard event listener
        document.removeEventListener('keydown', this.handleKeyPress);

        // Call onClose callback if provided
        if (this.onCloseCallback) {
            this.onCloseCallback();
        }

        // Clean up references
        this.modal = null;
        this.overlay = null;
        this.onCloseCallback = null;
    }

    /**
     * Handle keyboard events
     * @param {KeyboardEvent} e - Keyboard event
     */
    handleKeyPress(e) {
        if (e.key === 'Escape' && this.isOpen) {
            this.close();
        }
    }

    /**
     * Handle overlay click
     * @param {MouseEvent} e - Mouse event
     */
    handleOverlayClick(e) {
        if (e.target === this.overlay) {
            this.close();
        }
    }

    /**
     * Detect cross-promotion content in the page
     * @returns {string|null} Cross-promotion HTML content or null if not found or already shown
     */
    static detectCrossPromotion() {
        // Check if modal has already been shown this session
        try {
            const hasShownModal = sessionStorage.getItem('crossPromotionModalShown');
            if (hasShownModal) {
                return null;
            }
        } catch (e) {
            // sessionStorage not available, return null
            return null;
        }
        
        // Look for cross-promotion content in the dedicated cross-promotion container
        const crossPromoContainer = document.querySelector('.configurable-product__cross-promotion-container');
        
        if (!crossPromoContainer) {
            return null;
        }

        // Find cross-promotion element
        const crossPromoElement = crossPromoContainer.querySelector('.cross-promotion');
        
        if (!crossPromoElement) {
            return null;
        }

        // Return the HTML content
        return crossPromoElement.outerHTML;
    }
}

export default CrossPromotionModal;